<template>
  <b-modal
    v-model="showModal"
    :title="modalTitle"
    size="lg"
    :no-close-on-backdrop="busy"
    :no-close-on-esc="busy"
    @hidden="onHidden"
  >
    <!-- TTN console quick link -->
    <div class="mb-3 d-flex align-items-center">
      <b-button
        size="sm"
        variant="outline-primary"
        :href="ttnConsoleUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i class="fas fa-external-link-alt mr-1"></i> Open TTN Console
      </b-button>
      <small class="text-muted ml-2">
        Use this to copy the Application ID and API key from your TTN account.
      </small>
    </div>

    <!-- Load error -->
    <b-alert v-if="loadError" show variant="danger" class="mb-3">
      {{ loadError }}
    </b-alert>

    <!-- ================= Tabs ================= -->
    <b-tabs v-model="activeTab" content-class="mt-3">

      <!-- ================= Tab 1: Configuration (wizard) ================= -->
      <b-tab title="Configuration" name="config">

        <!-- Step indicator -->
        <div class="d-flex align-items-center mb-4">
          <div
            v-for="(step, idx) in steps"
            :key="step.id"
            class="d-flex align-items-center"
            :class="idx + 1 < currentStep ? 'step-clickable' : ''"
            @click="goToStep(idx + 1)"
          >
            <div
              class="rounded-circle d-flex align-items-center justify-content-center mr-2"
              :class="stepCircleClass(idx)"
              style="width: 32px; height: 32px; min-width: 32px;"
            >
              <i v-if="currentStep > idx + 1" class="fas fa-check"></i>
              <span v-else>{{ idx + 1 }}</span>
            </div>
            <span :class="stepLabelClass(idx)">{{ step.label }}</span>
            <i
              v-if="idx < steps.length - 1"
              class="fas fa-chevron-right mx-3 text-muted"
            ></i>
          </div>
        </div>

        <!-- ---------- Step 1: Application ---------- -->
        <div v-show="currentStep === 1">
          <b-form-group label="Display name" label-for="ttn-display-name">
            <b-form-input
              id="ttn-display-name"
              v-model.trim="form.display_name"
              placeholder="e.g. Acme TTN production"
              :disabled="busy"
            />
            <small class="text-muted">A label for your own reference. Not sent to TTN.</small>
          </b-form-group>

          <b-form-group label="Cluster" label-for="ttn-cluster">
            <b-form-select
              id="ttn-cluster"
              v-model="clusterChoice"
              :options="clusterOptions"
              :disabled="busy"
            />
            <small class="text-muted">
              The TTN cluster your application lives on. Look at the URL of your TTN
              console — it is the part before <code>.cloud.thethings.network</code>
              (e.g. <code>eu1</code>, <code>nam1</code>, <code>au1</code>).
              If unsure, leave it at <strong>eu1</strong>.
            </small>
          </b-form-group>

          <b-form-group
            v-if="clusterChoice === 'custom'"
            label="Custom base URL"
            label-for="ttn-base-url"
          >
            <b-form-input
              id="ttn-base-url"
              v-model.trim="form.base_url"
              placeholder="https://..."
              :disabled="busy"
            />
            <small class="text-muted">
              Full base URL of the TTN cluster, starting with <code>https://</code>
              and with no trailing slash.
            </small>
          </b-form-group>

          <b-form-group label="Application ID" label-for="ttn-app-id">
            <b-form-input
              id="ttn-app-id"
              v-model.trim="form.application_id"
              placeholder="my-ttn-application"
              :disabled="busy"
            />
            <small class="text-muted">
              In the TTN console: <strong>Applications</strong> → click your application.
              The <strong>Application ID</strong> is shown at the top of the page and
              in the URL. Lowercase letters, digits, and hyphens only.
            </small>
          </b-form-group>
        </div>

        <!-- ---------- Step 2: Credentials ---------- -->
        <div v-show="currentStep === 2">
          <b-form-group>
            <template #label>
              API key
              <b-badge
                v-if="integration && integration.api_key_set"
                variant="success"
                class="ml-2"
              >
                Saved
              </b-badge>
            </template>
            <b-input-group>
              <b-form-input
                id="ttn-api-key"
                v-model="form.api_key"
                :type="showApiKey ? 'text' : 'password'"
                :placeholder="apiKeyPlaceholder"
                autocomplete="new-password"
                :disabled="busy"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :disabled="busy"
                  @click="showApiKey = !showApiKey"
                  :title="showApiKey ? 'Hide API key' : 'Show API key'"
                >
                  <i :class="showApiKey ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <small class="text-muted">
            In the TTN console: <strong>Applications</strong> → your application →
            <strong>API keys</strong> → <strong>Create API key</strong>.
            Grant these rights:
          </small>
          <ul class="small text-muted mb-2 pl-3">
            <li><code>View application</code></li>
            <li><code>Read devices</code> and <code>Write devices</code></li>
            <li><code>Read device keys</code> and <code>Write device keys</code></li>
            <li><code>Read uplink traffic</code></li>
            <li><code>Write downlink traffic</code></li>
          </ul>
          <small class="text-muted d-block">
            TTN shows the key <strong>only once</strong> after creation — copy it
            here immediately. Stored encrypted and never returned by the API.
            <span v-if="integration && integration.api_key_set">
              A key is already saved. Leave this field empty to keep it, or type a
              new one to replace it.
            </span>
          </small>

          <b-alert
            v-if="!step2Valid"
            show
            variant="warning"
            class="mt-3"
          >
            <i class="fas fa-exclamation-triangle mr-1"></i>
            An API key is required because the cluster or application ID
            is being set for the first time or has changed.
          </b-alert>
        </div>

        <!-- ---------- Step 3: Review ---------- -->
        <div v-show="currentStep === 3">
          <p class="text-muted">
            Review the configuration below, then click
            <strong>Save and test</strong>.
          </p>

          <dl class="row mb-0">
            <dt class="col-sm-4">Display name</dt>
            <dd class="col-sm-8">{{ form.display_name || '—' }}</dd>

            <dt class="col-sm-4">Cluster</dt>
            <dd class="col-sm-8">{{ clusterLabel }}</dd>

            <dt class="col-sm-4">Application ID</dt>
            <dd class="col-sm-8">{{ form.application_id || '—' }}</dd>

            <dt class="col-sm-4">API key</dt>
            <dd class="col-sm-8">{{ apiKeyStatus }}</dd>
          </dl>

          <b-alert v-if="!formValid" show variant="warning" class="mt-3">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Some required fields are missing. Use <strong>Back</strong> to complete them.
          </b-alert>
        </div>

        <!-- ---------- Step navigation ---------- -->
        <div class="d-flex justify-content-between mt-4 pt-3 border-top">
          <base-button
            type="secondary"
            size="sm"
            :disabled="currentStep === 1 || busy"
            @click="prevStep"
          >
            <i class="fas fa-arrow-left mr-1"></i> Back
          </base-button>

          <div>
            <base-button
              v-if="currentStep < steps.length"
              type="primary"
              size="sm"
              :disabled="!canGoNext || busy"
              @click="nextStep"
            >
              Next <i class="fas fa-arrow-right ml-1"></i>
            </base-button>
            <base-button
              v-if="currentStep === steps.length"
              type="success"
              size="sm"
              :disabled="!formValid || busy"
              @click="onSave"
            >
              <span v-if="!saving">
                <i class="fas fa-save mr-1"></i> Save and test
              </span>
              <span v-else>
                <span v-if="testing">Saving and testing...</span>
                <span v-else>Saving...</span>
              </span>
            </base-button>
          </div>
        </div>
      </b-tab>

      <!-- ================= Tab 2: Connection test ================= -->
      <b-tab name="test">
        <template #title>
          Connection test
          <span
            v-if="integration && integration.status === 'ok'"
            class="text-success ml-1"
          >✓</span>
          <span
            v-else-if="integration && integration.status === 'not_tested'"
            class="text-info ml-1"
          >?</span>
          <span
            v-else-if="integration"
            class="text-warning ml-1"
          >!</span>
        </template>

        <!-- No config yet -->
        <div v-if="!integration" class="text-muted text-center py-4">
          <i class="fas fa-info-circle fa-2x mb-2"></i>
          <p class="mb-0">
            No configuration saved yet. Use the <strong>Configuration</strong> tab
            to set up the connection first.
          </p>
        </div>

        <!-- Config exists: show status -->
        <div v-else>
          <b-alert :show="true" :variant="statusVariant">
            <div class="font-weight-bold">
              <i :class="statusIcon" class="mr-1"></i>
              {{ statusLabel }}
            </div>
            <div v-if="integration.status_detail" class="small mt-1">
              {{ integration.status_detail }}
            </div>
            <ul
              v-if="integration.missing_rights && integration.missing_rights.length"
              class="mb-0 small pl-3 mt-2"
            >
              <li v-for="r in integration.missing_rights" :key="r">{{ r }}</li>
            </ul>
          </b-alert>

          <div class="small text-muted mb-3">
            <template v-if="integration.last_checked_at">
              <i class="fas fa-clock mr-1"></i>
              Last checked: {{ formatDate(integration.last_checked_at) }}
            </template>
            <template v-else>
              <i class="fas fa-clock mr-1"></i>
              Not tested yet — click <strong>Run test</strong> to verify the connection.
            </template>
          </div>

          <div class="text-right">
            <base-button
              type="info"
              size="sm"
              :disabled="busy"
              @click="onTest"
            >
              <span v-if="!testing">
                <i class="fas fa-vial mr-1"></i>
                {{ integration.last_checked_at ? 'Re-test connection' : 'Run test' }}
              </span>
              <span v-else>Testing...</span>
            </base-button>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <!-- ================= Footer ================= -->
    <template #modal-footer>
      <div class="d-flex w-100 justify-content-between">
        <div>
          <base-button
            v-if="integration"
            type="danger"
            size="sm"
            :disabled="busy"
            @click="onRemove"
          >
            <span v-if="!removing">Remove</span>
            <span v-else>Removing...</span>
          </base-button>
        </div>
        <div>
          <base-button
            type="secondary"
            size="sm"
            :disabled="busy"
            @click="showModal = false"
          >
            Close
          </base-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { Action } from "@/store/actions";

const CLUSTER_URLS = {
  eu1:  'https://eu1.cloud.thethings.network',
  nam1: 'https://nam1.cloud.thethings.network',
  au1:  'https://au1.cloud.thethings.network',
};

const CLUSTER_LABELS = {
  eu1:  'eu1 (Europe)',
  nam1: 'nam1 (North America)',
  au1:  'au1 (Australia)',
};

const APP_ID_RE = /^[a-z0-9]([a-z0-9-]{0,34}[a-z0-9])?$/;

export default {
  name: 'EntityTTNModal',

  data() {
    return {
      showModal: false,
      entityName: '',
      loading: false,
      saving: false,
      testing: false,
      removing: false,
      loadError: '',
      showApiKey: false,

      activeTab: 'config',
      currentStep: 1,
      steps: [
        { id: 'application', label: 'Application' },
        { id: 'credentials', label: 'Credentials' },
        { id: 'review',      label: 'Review' },
      ],

      clusterChoice: 'eu1',
      clusterOptions: [
        { value: 'eu1',    text: 'eu1 (Europe)' },
        { value: 'nam1',   text: 'nam1 (North America)' },
        { value: 'au1',    text: 'au1 (Australia)' },
        { value: 'custom', text: 'Custom URL' },
      ],

      form: {
        display_name: '',
        base_url: CLUSTER_URLS.eu1,
        application_id: '',
        api_key: '',
      },
    };
  },

  computed: {
    integration() {
      return this.$store.getters.ttnIntegration(this.entityName);
    },
    busy() {
      return this.saving || this.testing || this.removing;
    },
    modalTitle() {
      return this.entityName
        ? `LoRa / TTN Integration — ${this.entityName}`
        : 'LoRa / TTN Integration';
    },

    // ---------- Validation, split per step ----------
    step1Valid() {
      const f = this.form;
      if (!f.display_name) return false;
      if (!f.application_id || !APP_ID_RE.test(f.application_id)) return false;
      if (!f.base_url || !f.base_url.startsWith('https://')) return false;
      return true;
    },
    step2Valid() {
      // If we still have a stored key and the connection-relevant fields
      // did not change, the key can be left empty (keep the existing one).
      const needsKey = !this.integration
        || this.integration.base_url !== this.form.base_url
        || this.integration.application_id !== this.form.application_id;
      return !needsKey || Boolean(this.form.api_key);
    },
    canGoNext() {
      if (this.currentStep === 1) return this.step1Valid;
      if (this.currentStep === 2) return this.step2Valid;
      return true;
    },
    formValid() {
      return this.step1Valid && this.step2Valid;
    },

    // ---------- Review step display ----------
    clusterLabel() {
      if (this.clusterChoice === 'custom') {
        return `Custom: ${this.form.base_url}`;
      }
      return CLUSTER_LABELS[this.clusterChoice] || this.clusterChoice;
    },
    apiKeyStatus() {
      if (this.form.api_key) {
        return 'New key entered (will replace the stored key)';
      }
      if (this.integration && this.integration.api_key_set) {
        return 'Using the key already stored';
      }
      return 'No key provided';
    },

    // ---------- Status display ----------
    statusVariant() {
      const s = this.integration && this.integration.status;
      if (s === 'ok') return 'success';
      if (s === 'insufficient_rights') return 'warning';
      if (s === 'app_not_found' || s === 'auth_failed') return 'danger';
      if (s === 'unreachable') return 'warning';
      if (s === 'not_tested') return 'info';
      return 'secondary';
    },
    statusIcon() {
      const s = this.integration && this.integration.status;
      if (s === 'ok') return 'fas fa-check-circle';
      if (s === 'not_tested') return 'fas fa-question-circle';
      return 'fas fa-exclamation-circle';
    },
    statusLabel() {
      const s = this.integration && this.integration.status;
      const map = {
        ok: 'Connected',
        insufficient_rights: 'Connected, but the API key is missing required rights',
        app_not_found: 'Application not found on this cluster',
        auth_failed: 'The API key was rejected by TTN',
        unreachable: 'Could not reach the TTN cluster',
        not_tested: 'Not yet tested',
      };
      return map[s] || 'Unknown status';
    },
    apiKeyPlaceholder() {
      return this.integration && this.integration.api_key_set ? '••••••••' : 'NNSXS.…';
    },
    ttnConsoleUrl() {
      const cluster = this.clusterChoice === 'custom'
        ? this.form.base_url.replace(/\/+$/, '')
        : CLUSTER_URLS[this.clusterChoice] || CLUSTER_URLS.eu1;
      return `${cluster}/console/`;
    },
  },

  watch: {
    clusterChoice(newVal) {
      if (newVal !== 'custom' && CLUSTER_URLS[newVal]) {
        this.form.base_url = CLUSTER_URLS[newVal];
      }
    },
  },

  methods: {
    // ---------- Step visuals ----------
    stepCircleClass(idx) {
      if (this.currentStep === idx + 1) return 'bg-primary text-white';
      if (this.currentStep > idx + 1) return 'bg-success text-white';
      return 'bg-light text-muted';
    },
    stepLabelClass(idx) {
      if (this.currentStep === idx + 1) return 'text-primary font-weight-bold';
      if (this.currentStep > idx + 1) return 'text-success';
      return 'text-muted';
    },

    // ---------- Step navigation ----------
    nextStep() {
      if (!this.canGoNext || this.busy) return;
      if (this.currentStep < this.steps.length) this.currentStep += 1;
    },
    prevStep() {
      if (this.currentStep > 1 && !this.busy) this.currentStep -= 1;
    },
    goToStep(step) {
      // Only allow jumping backward (to already-visited steps).
      if (this.busy) return;
      if (step < 1 || step > this.steps.length) return;
      if (step > this.currentStep) return;
      this.currentStep = step;
    },

    // ---------- Lifecycle ----------
    show(entityName) {
      this.entityName = entityName;
      this.resetLocal();
      this.showModal = true;
      this.load();
    },

    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const data = await this.$store.dispatch(Action.ttn.load, this.entityName);
        if (data) {
          this.populateForm(data);
        } else {
          this.resetForm();
        }
      } catch (err) {
        this.loadError = this.errMsg(err);
      } finally {
        this.loading = false;
      }
    },

    populateForm(data) {
      this.form.display_name = data.display_name || '';
      this.form.application_id = data.application_id || '';
      this.form.base_url = data.base_url || '';
      this.form.api_key = '';
      this.showApiKey = false;
      const match = Object.entries(CLUSTER_URLS).find(([, url]) => url === data.base_url);
      this.clusterChoice = match ? match[0] : 'custom';
    },

    resetForm() {
      this.form = {
        display_name: '',
        base_url: CLUSTER_URLS.eu1,
        application_id: '',
        api_key: '',
      };
      this.clusterChoice = 'eu1';
      this.showApiKey = false;
    },

    // ---------- Actions ----------
    async onSave() {
      if (!this.formValid || this.busy) return;
      this.saving = true;
      try {
        const payload = { ...this.form };
        if (!payload.api_key && this.integration) {
          delete payload.api_key;
        }

        let data = await this.$store.dispatch(Action.ttn.save, {
          entity: this.entityName,
          data: payload,
        });
        this.form.api_key = '';
        this.showApiKey = false;

        if (data.status === 'not_tested') {
          this.testing = true;
          try {
            data = await this.$store.dispatch(Action.ttn.test, this.entityName);
          } finally {
            this.testing = false;
          }
        }

        if (data.status === 'ok') {
          this.$notify({
            type: 'success',
            title: 'Saved and verified',
            text: data.status_detail || 'Configuration saved. TTN connection verified.',
          });
        } else {
          this.$notify({
            type: 'warning',
            title: 'Saved, test warning',
            text: data.status_detail || 'Configuration saved but the connection test reported an issue.',
          });
        }

        // Jump to the Connection test tab to show the result
        this.activeTab = 'test';
      } catch (err) {
        // store's notifyApiError already toasted
      } finally {
        this.saving = false;
      }
    },

    async onTest() {
      if (this.busy || !this.integration) return;
      this.testing = true;
      try {
        const data = await this.$store.dispatch(Action.ttn.test, this.entityName);
        if (data.status === 'ok') {
          this.$notify({ type: 'success', title: 'Test passed', text: data.status_detail });
        } else {
          this.$notify({ type: 'warning', title: 'Test warning', text: data.status_detail });
        }
      } catch (err) {
        // store's notifyApiError already toasted
      } finally {
        this.testing = false;
      }
    },

    async onRemove() {
      if (this.busy) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remove the TTN integration for ${this.entityName}? LoRa devices will stop working until re-linked.`,
        {
          title: 'Confirm Remove',
          okVariant: 'danger',
          okTitle: 'Remove',
          cancelTitle: 'Cancel',
          centered: true,
        }
      );
      if (!confirmed) return;
      this.removing = true;
      try {
        await this.$store.dispatch(Action.ttn.remove, this.entityName);
        this.resetForm();
        this.$notify({
          type: 'success',
          title: 'Removed',
          text: 'TTN integration removed.',
        });
        this.showModal = false;
      } catch (err) {
        // store's notifyApiError already toasted
      } finally {
        this.removing = false;
      }
    },

    // ---------- Cleanup ----------
    onHidden() {
      this.resetLocal();
    },

    resetLocal() {
      this.loadError = '';
      this.loading = false;
      this.saving = false;
      this.testing = false;
      this.removing = false;
      this.activeTab = 'config';
      this.currentStep = 1;
      this.resetForm();
    },

    errMsg(err) {
      if (!err) return 'Unknown error';
      if (typeof err === 'string') return err;
      if (err.message) return err.message;
      return JSON.stringify(err);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
    },
  },
};
</script>

<style scoped>
.step-clickable {
  cursor: pointer;
}
.step-clickable:hover .step-label,
.step-clickable:hover span {
  text-decoration: underline;
}
</style>